import { Component, Inject, Input, OnInit } from '@angular/core';
import { of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { TaskActionsService } from 'src/app/care/inbox/task-actions/task-actions.service';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { BasicService } from '../../services/basic.service';
import { ErrorHandlingService } from '../../services/error-handling.service';
import { TaskStateInWsNotif } from '../../models/misc.model';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-task-and-message-actions-menu',
  templateUrl: './task-and-message-actions-menu.component.html',
  styleUrls: ['./task-and-message-actions-menu.component.scss'],
})
export class TaskAndMessageActionsMenuComponent implements OnInit {
  @Input() siteId: number;
  @Input() taskId: number;
  @Input() message: any;

  loadingActions: boolean;
  messageActionsList = [];
  taskActionsList = [];

  constructor(
    private basicService: BasicService,
    private errorHandlingService: ErrorHandlingService,
    private alcmeonContextService: AlcmeonContextService,
    private taskActionsService: TaskActionsService,
    @Inject('MessagesFactory') private messagesService: any
  ) {}

  ngOnInit(): void {
    this.taskActionsList = this.initTaskActionsList();
  }

  private initTaskActionsList() {
    const newTaskAction = (labelKey: string, onClick: any) => ({
      text: this.basicService.translate(labelKey),
      onClick,
    });

    const processAction = newTaskAction('user-profile/messages/task-action-process', () => this.processTask());
    const seeAction = newTaskAction('user-profile/messages/task-action-see', () => this.seeTask());
    const archiveAction = newTaskAction('user-profile/messages/task-action-archive', () => this.archiveTask());
    const result = [];
    const task = this.message.task;
    const state = task?.state || null;

    switch (state) {
      case TaskStateInWsNotif.Pending:
        result.push(processAction);
        result.push(archiveAction);
        break;
      case TaskStateInWsNotif.Assigned:
      case TaskStateInWsNotif.HelpRequested:
      case TaskStateInWsNotif.HelpAnswered:
      case TaskStateInWsNotif.Answered:
      case TaskStateInWsNotif.Archived:
      case TaskStateInWsNotif.AutoArchived:
        result.push(seeAction);
        break;
      case TaskStateInWsNotif.OnHold:
        result.push(processAction);
        break;
    }
    return result;
  }

  loadActions() {
    this.loadingActions = true;
    of(this.messagesService.listActions(this.alcmeonContextService.companyId, this.message.id, this.siteId))
      .pipe(
        this.errorHandlingService.catchAlertAndThrowHttpError(),
        map(result => result.data),
        finalize(() => (this.loadingActions = false))
      )
      .subscribe(actions => {
        this.messageActionsList = actions?.actions_allowed || [];
      });
  }

  doMessageAction(actionType) {
    this.messagesService
      .action(this.alcmeonContextService.companyId, this.message.id, this.siteId, actionType, this.taskId)
      .then(
        () => this.basicService.toastInformation('message/action/ok'),
        response => this.basicService.toastAlert(response?.data?.error || 'error/access')
      );
  }

  processTask() {
    const task = this.message.task;
    this.taskActionsService.assign(task.id, task.state).subscribe(
      () => {
        this.seeTask();
        this.basicService.toastInformation('task/actions/info/assign');
      },
      response => {
        if (response.data && response.data.error && response.data.error.reason === 'no-rights') {
          this.basicService.toastAlert('no-rights');
        } else {
          this.basicService.toastAlert('task/actions/error/assign');
        }
      }
    );
  }

  seeTask() {
    const taskId = this.message.task.id;
    const url = `${environment.frontendURLPrefix}#/${this.alcmeonContextService.companyId}/inbox/task/${this.message.task.id}`;
    const windowName = `task-${taskId}`;
    window.open(url, windowName);
  }

  archiveTask() {
    const task = this.message.task;
    const data = {
      acquire: task.state === 'pending',
      from_on_hold: task.state === 'on_hold',
    };
    this.taskActionsService.archive(task.id, data).subscribe({
      next: () => this.basicService.toastInformation('task/actions/info/archive'),
      error: () => this.basicService.toastAlert('task/actions/error/archive'),
    });
  }
}
